import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { Set, OrderedMap } from 'immutable';
import Fuse from 'fuse.js';
import { useDispatch, useSelector } from 'react-redux';
import GroupIdentifierIcon from '@components/GroupIdentifierIcon';
import { selectGroupCollection, updateGroupsInBulk } from '@actions/groups';
import styles from './AddToGroup.module.scss';
import { CheckIcon, GroupIcon, LoaderIcon, PlusIcon } from '@components/Icons';
import Occasion from '@support/Occasion';
import Group from '@models/Group';
import {
  selectArrayOfSelectedContacts,
  selectIsMultiselectView,
  selectSelectedContact,
  selectSelectedContactBatch,
} from '@actions/ui';
import Pill from '@components/Pill';
import Contact from '@models/Contact';
import SearchableOptionsModal from '../SearchableOptions';
import { ID } from '@app/types';
import { ModalButton } from '..';
import { metaTrigger } from '@support/keyboardHandler';

type NewGroupOption = { id: 'create' };
type Option = NewGroupOption | Group;

interface AddToGroupModalProps {
  closeModal: () => void;
  contact?: Contact;
}

function Action({ added = false }) {
  if (!added) {
    return <PlusIcon width={10} height={10} />;
  }
  return <CheckIcon width={10} height={10} customColor="#2686be" />;
}

const newGroupOption = (): [Option] => [{ id: 'create' }];

const GroupRow = ({ group, added }: { group: Group; added: boolean }) => (
  <>
    <div className={styles.action}>
      <Action added={added} />
    </div>
    <div className={styles.primary}>
      <GroupIdentifierIcon className={styles.color} group={group} />
      <span className={styles.groupTitle}>{group.title}</span>
    </div>
    <div className={styles.details}>
      <span className="information">
        <span className="information-item">
          <GroupIcon className="information-icon" />
          <span className="information-item-text">
            {group.count === undefined ? 1 : group.count}
          </span>
        </span>
        <span className="information-item">{group.created.format(Occasion.dateFormat)}</span>
      </span>
    </div>
  </>
);

const CreateGroupInput = ({ editing, ...inputProps }) => (
  <>
    <div className={styles.action}>
      <Action />
    </div>
    {editing ? (
      <input
        className="form-input-transparent"
        type="text"
        placeholder="Enter a new Group name"
        autoFocus
        {...inputProps}
      />
    ) : (
      'Create New Group'
    )}
  </>
);

export default function AddToGroupModal(props: AddToGroupModalProps) {
  const dispatch = useDispatch();
  const { items, updated } = useSelector(selectGroupCollection);
  const baseGroups = useMemo(() => items, []);
  const [bulkUpdate, setBulkUpdate] = useState({
    add_group_ids: Set<ID>(),
    remove_group_ids: Set<ID>(),
    create_groups: [],
  });

  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [newGroupName, setNewGroupName] = useState('');
  const [creatingNewGroup, setCreatingNewGroup] = useState(false);

  const isMultiselectView = useSelector(selectIsMultiselectView);
  const selectedContact = useSelector(selectSelectedContact);
  const selectedContactBatch = useSelector(selectSelectedContactBatch);
  const selectedContactsArr = useSelector(selectArrayOfSelectedContacts);

  const [contact, contactGroup, contactIDs] = useMemo(() => {
    const contact = props.contact || selectedContact;
    let contactGroup = null;
    let contactIDs = [contact?.id];

    if (selectedContactBatch.size > 1 && isMultiselectView) {
      contactGroup = selectedContactsArr;
      contactIDs = selectedContactsArr.map((contact) => contact.id);
    }

    return [contact, contactGroup, contactIDs];
  }, []);

  // these we operate on when adding/removing contacts or adding new groups
  // at the same time, we update the bulkUpdate object
  const [displayedGroups, setDisplayedGroups] = useState(baseGroups);
  const groupOptions = useMemo(() => displayedGroups.valueSeq().toArray(), [displayedGroups.size]);
  const [filteredOptions, setFilteredOptions] = useState(newGroupOption().concat(groupOptions));

  const fuse = useMemo(() => new Fuse(groupOptions, { keys: ['title'], threshold: 0.3 }), []);

  const modalInputRef = useRef(null);
  const setModalInputRef = useCallback((ref) => (modalInputRef.current = ref.current), []);

  const handleSearch = useCallback(
    (term: string) => {
      if (saving) return;
      const searchedOptions = fuse.search(term).map(({ item }) => item);

      if (searchedOptions.length === 0 && term.length > 0) {
        return setFilteredOptions(newGroupOption());
      } else if (term.length === 0) {
        return setFilteredOptions(newGroupOption().concat(groupOptions));
      }

      // @ts-ignore
      setFilteredOptions(searchedOptions.concat(newGroupOption()));
    },
    [groupOptions, saving],
  );

  const blurNewGroupNameInput = () => {
    setCreatingNewGroup(false);
    setNewGroupName('');
    modalInputRef?.current.focus();
  };

  const createNewGroup = () => {
    let newGroup = new Group({ title: newGroupName, contact_ids: contactIDs });
    setDisplayedGroups(
      OrderedMap({ [newGroup.id]: newGroup })
        .concat(displayedGroups)
        .toOrderedMap(),
    );
    setBulkUpdate({
      ...bulkUpdate,
      create_groups: [...bulkUpdate.create_groups, newGroup.title],
    });
    blurNewGroupNameInput();
  };

  const handleCreateKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      return blurNewGroupNameInput();
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();

      if (newGroupName) {
        createNewGroup();
      } else {
        return blurNewGroupNameInput();
      }
    }
  };

  const addContactsToGroup = (group: Group) => {
    const _group = displayedGroups.get(group.slug || group.id);
    const groupWithAdditions = _group.addContactIDs(contactIDs);
    setDisplayedGroups(displayedGroups.set(group.slug || group.id, groupWithAdditions));

    if (bulkUpdate.remove_group_ids.has(group.id)) {
      return setBulkUpdate({
        ...bulkUpdate,
        remove_group_ids: bulkUpdate.remove_group_ids.remove(group.id),
      });
    }

    setBulkUpdate({
      ...bulkUpdate,
      add_group_ids: bulkUpdate.add_group_ids.add(group.id),
    });
  };

  const removeContactsFromGroup = (group: Group) => {
    const _group = displayedGroups.get(group.slug || group.id);
    const groupWithRemovals = _group.removeContactIDSet(Set(contactIDs));
    setDisplayedGroups(displayedGroups.set(group.slug || group.id, groupWithRemovals));

    if (bulkUpdate.add_group_ids.has(group.id)) {
      return setBulkUpdate({
        ...bulkUpdate,
        add_group_ids: bulkUpdate.add_group_ids.remove(group.id),
      });
    }

    setBulkUpdate({
      ...bulkUpdate,
      remove_group_ids: bulkUpdate.remove_group_ids.add(group.id),
    });
  };

  const onSuggestionSelected = ({ id, slug }) => {
    if (saving) return;

    if (id !== 'create') {
      const group = displayedGroups.get(slug || id);

      if (group.hasAllContactIDs(contactIDs)) {
        removeContactsFromGroup(group);
      } else {
        addContactsToGroup(group);
      }
    } else if (!creatingNewGroup) {
      setCreatingNewGroup(true);
      setNewGroupName(searchTerm);
      setSearchTerm('');
      setFilteredOptions(filteredOptions.filter((option) => option.id === 'create'));
    } else if (newGroupName) {
      createNewGroup();
    }
  };

  const renderOption = useCallback(
    ({ id, slug }) => {
      if (id === 'create') {
        return (
          <CreateGroupInput
            editing={creatingNewGroup}
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            onBlur={blurNewGroupNameInput}
            onKeyDown={handleCreateKeyDown}
          />
        );
      }

      const group = displayedGroups.get(slug || id);
      return <GroupRow group={group} added={group.hasAllContactIDs(contactIDs)} />;
    },
    [displayedGroups, creatingNewGroup, newGroupName],
  );

  const saveChanges = () => {
    if (saving) return;
    dispatch(
      updateGroupsInBulk({
        add_group_ids: bulkUpdate.add_group_ids.toArray(),
        remove_group_ids: bulkUpdate.remove_group_ids.toArray(),
        create_groups: bulkUpdate.create_groups,
        contact_ids: contactIDs,
      }),
    );
    setSaving(true);
  };

  const handleInputKeyDown = (e) => {
    if (metaTrigger(e) && e.key === 'Enter') {
      saveChanges();
      e.stopPropagation();
      return true;
    }

    return false;
  };

  useEffect(() => {
    if (saving) {
      props.closeModal();
    }
  }, [updated]);

  useEffect(() => {
    fuse.setCollection(groupOptions);
    setFilteredOptions(newGroupOption().concat(groupOptions));
  }, [groupOptions]);

  return (
    <SearchableOptionsModal
      options={filteredOptions}
      onOptionSelected={onSuggestionSelected}
      overlayClassName={cn(styles.addToGroupModal, saving && styles.saving)}
      closeModal={props.closeModal}
      placeholder={
        creatingNewGroup && newGroupName
          ? `Hit "Enter" to create "${newGroupName}"`
          : 'Search for a Group'
      }
      onInputKeyDown={handleInputKeyDown}
      handleSearch={handleSearch}
      renderOption={renderOption}
      afterInput={<Pill contact={contact} contactGroup={contactGroup} maxWidth={160} />}
      setInputRef={setModalInputRef}
      searchTerm={searchTerm}
      setSearchTerm={setSearchTerm}
      afterOptions={
        <div className={styles.buttons}>
          {saving ? (
            <div className={styles.savingMessage}>
              Saving changes <LoaderIcon />
            </div>
          ) : (
            <>
              <ModalButton className={styles.cancelBtn} onClick={props.closeModal} variant="muted">
                Cancel
              </ModalButton>
              <ModalButton
                disabled={
                  bulkUpdate.add_group_ids.size === 0 &&
                  bulkUpdate.remove_group_ids.size === 0 &&
                  bulkUpdate.create_groups.length === 0
                }
                onClick={saveChanges}
                variant="v2"
              >
                Save
              </ModalButton>
            </>
          )}
        </div>
      }
    />
  );
}
